import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Constantes } from 'src/app/comun/data/constantes';
import { BancoldexService } from 'src/app/comun/services/bancoldex.service';
import { MicroempresarioService } from 'src/app/microempresario/services/microempresario.service';
import { ParametrosService } from 'src/app/services/parametros.service';
import { SweetAlertService } from 'src/app/services/sweet-alert.service';
import {
  numeroDocumentoPorTipoDocValidator,
  TipoDocIdentidadParaValidacion,
} from 'src/app/shared/utils/form-validators.util';
import { SharedModule } from 'src/app/shared/shared.module';
import { HomeCuentaRegistroService } from '../../../services/home-cuenta-registro.service';
import { HomeCuentaCampaniaService } from '../../../services/home-cuenta-campania.service';
import {
  isCampaniaResponseOk,
  mapCampaniaJuridicaExtras,
  mapCampaniaJuridicaToHomeForm,
  resolveTipoDocOptionValue,
} from '../../../utils/home-cuenta-campania.mapper';
import { HomeCuentaFormFooterComponent } from '../home-cuenta-form-footer/home-cuenta-form-footer.component';
import {
  celularErrorMessage,
  correoErrorMessage,
  documentoErrorMessage,
  dominioCorreoBloqueadoValidator,
  HOME_EMAIL_PATTERN,
  HOME_PASSWORD_HINT,
  HOME_PASSWORD_PATTERN,
  isFieldInvalid,
  matchFields,
  passwordErrorMessage,
  requiredErrorMessage,
  atLeastOneSelected,
} from '../home-cuenta-form.validators';

declare var $: any;

@Component({
  selector: 'app-home-cuenta-form-juridica',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    HomeCuentaFormFooterComponent,
  ],
  templateUrl: './home-cuenta-form-juridica.component.html',
  styleUrls: ['./home-cuenta-form-juridica.component.scss'],
})
export class HomeCuentaFormJuridicaComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly idPrefix = 'home-cuenta-pj';
  readonly passwordHint = HOME_PASSWORD_HINT;
  pasoActual = 1;
  submitting = false;
  showPassword = false;
  showConfirmPassword = false;
  passwordFieldActive = false;
  hasVisibleErrors = false;
  private campaniaPrefillDone = false;
  private documentosListos = false;
  private pendingCampaniaPatch: ReturnType<
    typeof mapCampaniaJuridicaToHomeForm
  > = null;

  @Output() readonly visibleErrorsChange = new EventEmitter<boolean>();

  documentos: Array<TipoDocIdentidadParaValidacion & { nombre?: string }> = [];
  tiposContacto: Array<{ idTipoContacto: number; nombre: string }> = [];
  fuentes: Array<{ idFuenteNeocredito: number; fuente: string }> = [];
  /** Solo jurídicas (registro manual). */
  private fuentesJuridica: Array<{ idFuenteNeocredito: number; fuente: string }> =
    [];
  private fuentesListas = false;
  /**
   * Campaña BX: la procedencia se elige al crear la campaña sin filtrar por
   * tipo de persona → el select usa catálogo combinado (PJ + PN).
   */
  private procedenciasSinFiltroCampania = false;
  private dominiosCorreoBloqueados: string[] = [];
  private readonly subs = new Subscription();
  private footerChecksSub?: Subscription;

  readonly step1Form = this.fb.nonNullable.group(
    {
      primerNombre: ['', [Validators.required, Validators.maxLength(20)]],
      segundoNombre: ['', Validators.maxLength(20)],
      primerApellido: ['', [Validators.required, Validators.maxLength(20)]],
      segundoApellido: ['', Validators.maxLength(20)],
      celular: [
        '',
        [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(10),
          Validators.pattern(/^\d{10}$/),
        ],
      ],
      correo: [
        '',
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern(HOME_EMAIL_PATTERN),
          dominioCorreoBloqueadoValidator(() => this.dominiosCorreoBloqueados),
        ],
      ],
      tipoDoc: ['', Validators.required],
      numDoc: [
        '',
        [
          Validators.required,
          Validators.maxLength(15),
          numeroDocumentoPorTipoDocValidator(() => this.documentos, 'tipoDoc'),
        ],
      ],
      confirmaDoc: [
        '',
        [
          Validators.required,
          Validators.maxLength(15),
          numeroDocumentoPorTipoDocValidator(() => this.documentos, 'tipoDoc'),
        ],
      ],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(HOME_PASSWORD_PATTERN),
        ],
      ],
      confirmaPassword: ['', Validators.required],
    },
    {
      validators: [
        matchFields('numDoc', 'confirmaDoc', 'confirmDocMismatch'),
        matchFields('password', 'confirmaPassword', 'confirmPasswordMismatch'),
      ],
    }
  );

  readonly step2Form = this.fb.nonNullable.group({
    nit: [
      '',
      [
        Validators.required,
        Validators.minLength(9),
        Validators.maxLength(9),
        Validators.pattern(/^\d{9}$/),
      ],
    ],
    /** Calculado automáticamente desde el NIT (módulo 11 DIAN); no editable. */
    dv: [{ value: '', disabled: true }, [Validators.required, Validators.pattern(/^\d$/)]],
    personaDeContacto: ['', Validators.required],
    tipoPersonaOtro: ['', Validators.maxLength(30)],
    contacto: [[] as Array<string | number>, atLeastOneSelected()],
    origen: ['', Validators.required],
  });

  @ViewChild('footer')
  private footer?: HomeCuentaFormFooterComponent;

  constructor(
    private readonly fb: FormBuilder,
    private readonly parametros: ParametrosService,
    private readonly microempresario: MicroempresarioService,
    private readonly registro: HomeCuentaRegistroService,
    private readonly sweetAlert: SweetAlertService,
    private readonly bancoldex: BancoldexService,
    private readonly campania: HomeCuentaCampaniaService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.cargarCatalogos();
    this.cargarDominiosCorreoBloqueados();
    this.subs.add(
      this.step1Form.get('tipoDoc')!.valueChanges.subscribe(() => {
        this.step1Form.get('numDoc')?.setValue('', { emitEvent: false });
        this.step1Form.get('confirmaDoc')?.setValue('', { emitEvent: false });
        this.step1Form.get('numDoc')?.markAsPristine();
        this.step1Form.get('numDoc')?.markAsUntouched();
        this.step1Form.get('confirmaDoc')?.markAsPristine();
        this.step1Form.get('confirmaDoc')?.markAsUntouched();
        this.step1Form.get('numDoc')?.updateValueAndValidity({ emitEvent: false });
        this.step1Form.get('confirmaDoc')?.updateValueAndValidity({ emitEvent: false });
        this.syncVisibleErrors();
      })
    );
    this.subs.add(
      this.step1Form.statusChanges.subscribe(() => this.syncVisibleErrors())
    );
    this.subs.add(
      this.step2Form.statusChanges.subscribe(() => this.syncVisibleErrors())
    );
    this.subs.add(
      this.step2Form.get('nit')!.valueChanges.subscribe((val) => {
        this.actualizarDvDesdeNit(val);
      })
    );
    this.subs.add(
      this.step2Form.get('personaDeContacto')!.valueChanges.subscribe(() => {
        this.actualizarValidadoresCargoOtro();
        // El selectpicker debe recalcular ancho al pasar a fila completa.
        setTimeout(() => this.refreshContactoSelectpicker(), 0);
      })
    );
    this.subs.add(
      this.campania.params$.subscribe((params) => {
        if (params?.perfil === 'juridica' && !this.campaniaPrefillDone) {
          this.cargarPrefillCampania(params.idCampania, params.token);
        }
      })
    );
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      this.bindFooterChecks();
      this.refreshOrigenSelectpicker();
      this.refreshContactoSelectpicker();
    });
  }

  ngOnDestroy(): void {
    this.destroyOrigenSelectpicker();
    this.destroyContactoSelectpicker();
    this.footerChecksSub?.unsubscribe();
    this.subs.unsubscribe();
  }

  @HostListener('focusout')
  onFocusOut(): void {
    queueMicrotask(() => this.syncVisibleErrors());
  }

  get registroCompleto(): boolean {
    return this.step1Form.valid && this.step2Form.valid;
  }

  get mostrarCargoOtro(): boolean {
    return this.step2Form.get('personaDeContacto')?.value === 'Otros';
  }

  isInvalidStep1(key: string): boolean {
    return isFieldInvalid(this.step1Form, key);
  }

  isInvalidStep2(key: string): boolean {
    return isFieldInvalid(this.step2Form, key);
  }

  errorMsgStep1(key: string): string {
    const c = this.step1Form.get(key);
    switch (key) {
      case 'correo':
        return correoErrorMessage(c);
      case 'celular':
        return celularErrorMessage(c);
      case 'numDoc':
      case 'confirmaDoc':
        return documentoErrorMessage(c);
      case 'password':
      case 'confirmaPassword':
        return passwordErrorMessage(c);
      default:
        return requiredErrorMessage(c);
    }
  }

  errorMsgStep2(key: string): string {
    const c = this.step2Form.get(key);
    if (key === 'nit' && c?.errors && !c.hasError('required')) {
      return 'El NIT debe tener 9 dígitos';
    }
    return requiredErrorMessage(c);
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPassword(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  onPasswordFocus(): void {
    this.passwordFieldActive = true;
  }

  onPasswordBlur(): void {
    this.passwordFieldActive = false;
  }

  onCorreoInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const lower = input.value.toLowerCase();
    if (input.value !== lower) {
      this.step1Form.get('correo')?.setValue(lower, { emitEvent: false });
    }
  }

  continuar(): void {
    if (this.step1Form.invalid) {
      this.step1Form.markAllAsTouched();
      this.syncVisibleErrors();
      return;
    }
    this.pasoActual = 2;
    this.syncVisibleErrors();
    queueMicrotask(() => {
      this.bindFooterChecks();
      this.refreshOrigenSelectpicker();
      this.refreshContactoSelectpicker();
    });
  }

  volver(): void {
    this.pasoActual = 1;
    this.syncVisibleErrors();
  }

  reset(): void {
    this.pasoActual = 1;
    this.step1Form.reset();
    this.step2Form.reset({
      nit: '',
      dv: '',
      personaDeContacto: '',
      tipoPersonaOtro: '',
      contacto: [] as Array<string | number>,
      origen: '',
    });
    this.actualizarValidadoresCargoOtro();
    this.submitting = false;
    this.showPassword = false;
    this.showConfirmPassword = false;
    this.passwordFieldActive = false;
    queueMicrotask(() => {
      this.footer?.resetChecks();
      this.syncVisibleErrors();
      this.refreshContactoSelectpicker();
      this.refreshOrigenSelectpicker();
    });
  }

  /** Quita marcas de error/touched sin borrar valores (cambio de tab). */
  clearValidationState(): void {
    this.passwordFieldActive = false;
    this.step1Form.markAsPristine();
    this.step1Form.markAsUntouched();
    this.step2Form.markAsPristine();
    this.step2Form.markAsUntouched();
    Object.values(this.step1Form.controls).forEach((control) => {
      control.markAsPristine();
      control.markAsUntouched();
    });
    Object.values(this.step2Form.controls).forEach((control) => {
      control.markAsPristine();
      control.markAsUntouched();
    });
    this.footer?.clearValidationState();
    this.syncVisibleErrors();
    this.refreshOrigenSelectpicker();
    this.refreshContactoSelectpicker();
  }

  /**
   * Equivalente a selectpikerChangeContactos del registro legacy.
   */
  selectpickerChangeContactos(): void {
    const fieldId = `${this.idPrefix}-contacto`;
    const $el = $(`#${fieldId}`);
    const tiposContactoElement = document.getElementById(fieldId);
    if (!tiposContactoElement || !$el?.length) {
      return;
    }

    this.step2Form.get('contacto')?.markAsDirty();
    this.step2Form.get('contacto')?.markAsTouched();
    this.syncVisibleErrors();

    const invalid = this.isInvalidStep2('contacto');
    const $wrap = $el.parent().hasClass('bootstrap-select') ? $el.parent() : null;
    if (invalid) {
      tiposContactoElement.classList.add('is-invalid');
      $wrap?.addClass('is-invalid');
    } else {
      tiposContactoElement.classList.remove('is-invalid');
      $wrap?.removeClass('is-invalid');
    }
    this.styleSelectpickerDropdown($el, 'home-cuenta-contacto-dropdown');
  }

  onSubmit(): void {
    if (!this.registroCompleto || !this.footer?.canSubmit || this.submitting) {
      this.step1Form.markAllAsTouched();
      this.step2Form.markAllAsTouched();
      this.footer?.checksForm.markAllAsTouched();
      this.syncVisibleErrors();
      this.refreshOrigenSelectpicker();
      this.selectpickerChangeContactos();
      return;
    }

    this.submitting = true;
    const tratamientoDatos = Boolean(this.footer.checksForm.get('datos')?.value);
    const payload = {
      ...this.step1Form.getRawValue(),
      ...this.step2Form.getRawValue(),
    };

    this.registro.registrarJuridica(payload, tratamientoDatos).subscribe({
      next: () => {
        this.submitting = false;
      },
      error: (err) => {
        this.submitting = false;
        this.sweetAlert.errorModal(
          'Error!',
          true,
          err?.error || err?.message || 'No fue posible completar el registro'
        );
      },
    });
  }

  private bindFooterChecks(): void {
    this.footerChecksSub?.unsubscribe();
    if (!this.footer) {
      return;
    }
    this.footerChecksSub = this.footer.checksForm.statusChanges.subscribe(() =>
      this.syncVisibleErrors()
    );
  }

  private syncVisibleErrors(): void {
    const activeForm = this.pasoActual === 1 ? this.step1Form : this.step2Form;
    const formHas = Object.values(activeForm.controls).some(
      (control) => (control.touched || control.dirty) && control.invalid
    );
    const footerHas =
      this.pasoActual === 2 && this.footer
        ? Object.values(this.footer.checksForm.controls).some(
            (control) => (control.touched || control.dirty) && control.invalid
          )
        : false;
    const next = formHas || footerHas;
    if (next === this.hasVisibleErrors) {
      return;
    }
    this.hasVisibleErrors = next;
    this.visibleErrorsChange.emit(next);
  }

  private cargarCatalogos(): void {
    this.parametros.getTipoDocumentos().subscribe((data: any) => {
      if (!data) {
        return;
      }
      this.documentos = data.filter(
        (documento: any) =>
          documento.personaJuridica == 0 &&
          documento.idTipoDocIdentidad != 4 &&
          documento.idTipoDocIdentidad != 6
      );
      this.documentosListos = true;
      this.step1Form.get('numDoc')?.updateValueAndValidity({ emitEvent: false });
      this.step1Form.get('confirmaDoc')?.updateValueAndValidity({ emitEvent: false });
      this.aplicarPrefillCampaniaPendiente();
    });

    this.microempresario.getListaTipoContacto().subscribe((data: any) => {
      this.tiposContacto = Array.isArray(data) ? data : data?.dtos || [];
      setTimeout(() => this.refreshContactoSelectpicker(), 0);
    });

    this.microempresario
      .getFuentes({
        tiposPersona: [
          { idTipoPersonaJuridica: Constantes.TIPO_PERSONA.JURIDICA },
        ],
      })
      .subscribe((data: any) => {
        this.fuentesJuridica = data?.fuentes || data?.dtos || [];
        if (!this.procedenciasSinFiltroCampania) {
          this.fuentes = [...this.fuentesJuridica];
        }
        this.fuentesListas = true;
        setTimeout(() => this.refreshOrigenSelectpicker(), 0);
        this.aplicarPrefillCampaniaPendiente();
      });
  }

  /** Catálogo PJ + PN formal/informal para precarga de campaña. */
  private aplicarFuentesCombinadasCampania(afterApply?: () => void): void {
    this.microempresario
      .getFuentes({
        tiposPersona: [
          { idTipoPersonaJuridica: Constantes.TIPO_PERSONA.JURIDICA },
          { idTipoPersonaJuridica: Constantes.TIPO_PERSONA.NATURAL_FORMAL },
          { idTipoPersonaJuridica: Constantes.TIPO_PERSONA.NATURAL_INFORMAL },
        ],
      })
      .subscribe({
        next: (data: any) => {
          const list = data?.fuentes || data?.dtos || [];
          const seen = new Set<number>();
          const merged: Array<{ idFuenteNeocredito: number; fuente: string }> =
            [];
          for (const f of list) {
            const id = Number(f.idFuenteNeocredito);
            if (!Number.isFinite(id) || seen.has(id)) {
              continue;
            }
            seen.add(id);
            merged.push(f);
          }
          this.fuentes = merged;
          this.cdr.detectChanges();
          setTimeout(() => {
            this.refreshOrigenSelectpicker();
            afterApply?.();
          }, 0);
        },
        error: () => {
          // Fallback: al menos jurídicas ya cacheadas.
          this.fuentes = [...this.fuentesJuridica];
          this.cdr.detectChanges();
          setTimeout(() => {
            this.refreshOrigenSelectpicker();
            afterApply?.();
          }, 0);
        },
      });
  }

  private cargarPrefillCampania(idCampania: string, token: string): void {
    this.campaniaPrefillDone = true;
    this.microempresario
      .cargarRegistroJuridico({ idCampania, token })
      .subscribe((response: any) => {
        if (!isCampaniaResponseOk(response)) {
          this.campania.clear();
          this.sweetAlert.errorModal(
            'Error!',
            true,
            response?.error ||
              response?.mensaje ||
              'No se encontró la campaña'
          );
          return;
        }
        const patch = mapCampaniaJuridicaToHomeForm(response?.data);
        if (!patch) {
          this.campania.clear();
          return;
        }
        this.campania.setExtrasJuridica(
          mapCampaniaJuridicaExtras(response?.data)
        );
        this.pendingCampaniaPatch = patch;
        this.aplicarPrefillCampaniaPendiente();
      });
  }

  private aplicarPrefillCampaniaPendiente(): void {
    const patch = this.pendingCampaniaPatch;
    if (!patch || !this.documentosListos || !this.fuentesListas) {
      return;
    }
    this.pendingCampaniaPatch = null;
    this.campania.clear();

    const tipoDoc = resolveTipoDocOptionValue(
      this.documentos,
      patch.step1.tipoDoc
    );
    const origenCampania = String(patch.step2.origen ?? '').trim();

    const aplicarCampos = (): void => {
      this.step1Form.patchValue(
        {
          primerNombre: patch.step1.primerNombre,
          segundoNombre: patch.step1.segundoNombre,
          primerApellido: patch.step1.primerApellido,
          segundoApellido: patch.step1.segundoApellido,
          celular: patch.step1.celular,
          correo: patch.step1.correo,
          numDoc: patch.step1.numDoc,
          confirmaDoc: patch.step1.confirmaDoc,
        },
        { emitEvent: false }
      );
      this.step2Form.patchValue(patch.step2, { emitEvent: false });
      this.actualizarDvDesdeNit(patch.step2.nit);
      this.cdr.detectChanges();

      setTimeout(() => {
        if (tipoDoc !== '' && tipoDoc != null) {
          this.step1Form
            .get('tipoDoc')
            ?.setValue(String(tipoDoc), { emitEvent: false });
        }
        this.step1Form
          .get('numDoc')
          ?.setValue(patch.step1.numDoc, { emitEvent: false });
        this.step1Form
          .get('confirmaDoc')
          ?.setValue(patch.step1.confirmaDoc, { emitEvent: false });
        if (origenCampania) {
          this.step2Form
            .get('origen')
            ?.setValue(origenCampania, { emitEvent: false });
        }
        this.refreshOrigenSelectpicker();
        this.refreshContactoSelectpicker();
        this.cdr.detectChanges();
      }, 100);
    };

    // Campaña: procedencia de BX puede ser PN → catálogo combinado.
    if (origenCampania) {
      this.procedenciasSinFiltroCampania = true;
      this.aplicarFuentesCombinadasCampania(aplicarCampos);
      return;
    }

    aplicarCampos();
  }

  private refreshOrigenSelectpicker(): void {
    const $el = $(`#${this.idPrefix}-origen`);
    if (!$el?.length || typeof $el.selectpicker !== 'function') {
      return;
    }
    if ($el.parent().hasClass('bootstrap-select')) {
      $el.selectpicker('destroy');
    }
    if (this.isInvalidStep2('origen')) {
      $el.addClass('is-invalid');
    } else {
      $el.removeClass('is-invalid');
    }
    $el.addClass('selectpicker');
    $el.selectpicker({
      container: 'body',
      liveSearch: true,
      size: 6,
      width: '100%',
      dropupAuto: true,
      noneSelectedText: 'Selecciona una opción',
    });
    const origen = this.step2Form.get('origen')?.value;
    if (origen != null && String(origen).length > 0) {
      $el.selectpicker('val', String(origen));
    }
    $el
      .off('shown.bs.select.homeCuentaOrigen')
      .on('shown.bs.select.homeCuentaOrigen', () =>
        this.styleSelectpickerDropdown($el, 'home-cuenta-origen-dropdown')
      );
    this.styleSelectpickerDropdown($el, 'home-cuenta-origen-dropdown');
  }

  private normalizeContactoIds(value: unknown): Array<number> {
    const raw: unknown[] = Array.isArray(value)
      ? value
      : value != null && String(value).length > 0
        ? [value]
        : [];
    return raw
      .map((item) => Number(item))
      .filter((n) => !Number.isNaN(n) && n > 0);
  }

  private refreshContactoSelectpicker(): void {
    const $el = $(`#${this.idPrefix}-contacto`);
    if (!$el?.length || typeof $el.selectpicker !== 'function') {
      return;
    }
    const currentValue = this.step2Form.get('contacto')?.value;

    if ($el.parent().hasClass('bootstrap-select')) {
      $el.selectpicker('destroy');
    }
    if (this.isInvalidStep2('contacto')) {
      $el.addClass('is-invalid');
    } else {
      $el.removeClass('is-invalid');
    }
    $el.addClass('selectpicker');
    $el.selectpicker({
      container: 'body',
      liveSearch: true,
      size: 4,
      width: '100%',
      dropupAuto: true,
      actionsBox: true,
      selectAllText: "<i class='fas fa-plus'></i> Seleccionar",
      deselectAllText: "<i class='fas fa-minus'></i> Quitar todos",
      title: 'Selecciona una opción',
      noneSelectedText: 'Selecciona una opción',
      selectedTextFormat: 'count > 2',
      countSelectedText: '{0} seleccionados',
    });

    const selectedIds = this.normalizeContactoIds(currentValue);
    if (selectedIds.length > 0) {
      $el.selectpicker('val', selectedIds.map(String));
    }

    $el
      .off('changed.bs.select.homeCuentaContacto shown.bs.select.homeCuentaContacto')
      .on('shown.bs.select.homeCuentaContacto', () =>
        this.styleSelectpickerDropdown($el, 'home-cuenta-contacto-dropdown')
      );
    this.styleSelectpickerDropdown($el, 'home-cuenta-contacto-dropdown');
  }

  private styleSelectpickerDropdown($el: JQuery | any, menuClass: string): void {
    const picker = $el.data('selectpicker');
    if (!picker?.$menu?.length) {
      return;
    }
    const btnWidth = picker.$newElement?.outerWidth?.() || $el.outerWidth();
    const $menu = picker.$menu;
    $menu.addClass(menuClass);
    if (btnWidth) {
      $menu[0].style.setProperty('--home-cuenta-origen-width', `${btnWidth}px`);
      $menu[0].style.setProperty('overflow-x', 'hidden', 'important');
    }
    $menu.find('.inner').css({ overflowX: 'hidden', maxWidth: '100%' });
    $menu.find('li a, li a span.text').css({
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
    });
    // Por si queda un bs-title-option residual tras destroy/reinit
    $menu.find('a.bs-title-option').closest('li').hide();
  }

  private destroyOrigenSelectpicker(): void {
    const $el = $(`#${this.idPrefix}-origen`);
    $el.off('shown.bs.select.homeCuentaOrigen');
    if ($el?.length && $el.parent().hasClass('bootstrap-select')) {
      $el.selectpicker('destroy');
    }
  }

  private destroyContactoSelectpicker(): void {
    const $el = $(`#${this.idPrefix}-contacto`);
    $el.off('shown.bs.select.homeCuentaContacto');
    if ($el?.length && $el.parent().hasClass('bootstrap-select')) {
      $el.selectpicker('destroy');
    }
  }

  private cargarDominiosCorreoBloqueados(): void {
    this.bancoldex
      .getFilterParametro({
        idParametro: 'PAR-160',
        nombre: null,
        isParFuncional: true,
        pagina: 1,
      })
      .subscribe({
        next: (response: any) => {
          const valor = response?.data?.[0]?.valorParametro;
          if (typeof valor === 'string' && valor.trim()) {
            this.dominiosCorreoBloqueados = valor
              .split(',')
              .map((d: string) => d.trim().toLowerCase())
              .filter((d: string) => d.length > 0);
          }
          this.step1Form.get('correo')?.updateValueAndValidity({ emitEvent: false });
        },
        error: () => {
          // Sin PAR-160 se valida solo formato
        },
      });
  }

  /** Mismo patrón que actualizar-datos PJ: cargo libre solo si eligen "Otros". */
  private actualizarValidadoresCargoOtro(): void {
    const cargoCtrl = this.step2Form.get('tipoPersonaOtro');
    if (!cargoCtrl) {
      return;
    }
    if (this.mostrarCargoOtro) {
      cargoCtrl.setValidators([Validators.required, Validators.maxLength(30)]);
    } else {
      cargoCtrl.setValidators(Validators.maxLength(30));
      cargoCtrl.setValue('', { emitEvent: false });
    }
    cargoCtrl.updateValueAndValidity({ emitEvent: false });
    this.syncVisibleErrors();
  }

  private actualizarDvDesdeNit(nit: string | null | undefined): void {
    const nitSinDV = String(nit || '').replace(/\D/g, '').slice(0, 9);
    const dvCtrl = this.step2Form.get('dv');
    if (!nitSinDV) {
      dvCtrl?.setValue('', { emitEvent: false });
      return;
    }
    dvCtrl?.setValue(String(this.calcularDigitoVerificacionNIT(nitSinDV)), {
      emitEvent: false,
    });
  }

  /**
   * Cálculo del dígito de verificación NIT (Colombia) según algoritmo módulo 11 DIAN.
   * Pesos de derecha a izquierda: 3, 7, 13, 17, 19, 23, 29, 37, 41.
   */
  private calcularDigitoVerificacionNIT(nitSinDV: string): number {
    const digits = nitSinDV.padStart(9, '0').slice(-9).split('').map(Number);
    const pesos = [41, 37, 29, 23, 19, 17, 13, 7, 3];
    const sum = digits.reduce((s, d, i) => s + d * pesos[i], 0);
    const remainder = sum % 11;
    return remainder <= 1 ? remainder : 11 - remainder;
  }
}
